import React, { useEffect, useState, useRef, useContext } from "react";

import {
  ArrowBack,
  AutoFixHighRounded,
  CloudDownload,
  Send,
  Share,
  Summarize,
  Edit,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import {
  CircularProgress,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Chip,
  Skeleton,
  Tooltip,
  Fab,
} from "@mui/material";
import {
  GetSubmissionResponsePortal,
  GetSurveyResponse,
  OrgConfigModel,
  SubmissionVersionTime,
  Template,
  getOrgConfigPortal,
  getOrgTemplates,
  getSubmissionPortal,
  getSubmissionSynopsisPortal,
  getSurvey,
  patchSubmissionPortal,
  postSubmissionExportEvidencePortal,
  postSubmissionGenerateIncidentPortal,
  postSubmissionStatusPortal,
  postSubmissionUpdateNotificationPortal,
} from "@rubi/api-generated";
import { PageHeader } from "@rubi/ui";
import { toast } from "@rubi/ui";
import { buildAttachmentIdMap } from "@rubi/utils";
import { useNavigate, useParams } from "react-router-dom";
import { Model, FunctionFactory } from "survey-core";
import { Survey } from "survey-react-ui";

import BottomFixedActionContainer from "@/common/BottomFixedActionContainer";
import {
  describeIncidentNumberFormat,
  getIncidentNumberFormat,
  validateIncidentNumber,
} from "@/common/incidentNumberFormat";
import { getServiceConfig } from "@/common/serviceConfigurations";
import StatusActionModal from "@/common/StatusActionModal";
import { formatDateShort } from "@/common/utils";
import { OnlineReportingContext } from "@/components/online-reporting/context";
import { registerAutocomplete } from "@/components/online-reporting/custom-questions/AddressAutocompleteQuestion/AddressAutocompleteQuestion";
import { registerCameraRegistryMap } from "@/components/online-reporting/custom-questions/CameraRegistryQuestion/CameraRegistryQuestion";
import { registerCurrencyInput } from "@/components/online-reporting/custom-questions/CurrencyInputQuestion/CurrencyInputQuestion";
import {
  FilePreviewConfigContext,
  useFilePreviewConfig,
} from "@/components/online-reporting/custom-questions/CustomQuestionFile";
import { registerDriversLicenseScanner } from "@/components/online-reporting/custom-questions/DriversLicenseScannerQuestion";
import { registerEnhancedFileUpload } from "@/components/online-reporting/custom-questions/EnhancedFileUploadQuestion/EnhancedFileUploadQuestion";
import { registerGasQuantityInput } from "@/components/online-reporting/custom-questions/GasQuantityInputQuestion/GasQuantityInputQuestion";
import { registerNarrativeCanvas } from "@/components/online-reporting/custom-questions/NarrativeCanvasQuestion/NarrativeCanvasQuestion";
import AssignedToSelector from "@/components/online-reporting/report-view/AssignedToSelector";
import ShareReportDialog from "@/components/online-reporting/report-view/ShareReportDialog";
import {
  applyTranslation,
  hasForeignLocale,
} from "@/components/online-reporting/report-view/translation";
import TranslationToggle from "@/components/online-reporting/report-view/TranslationToggle";
import { isReportClosed } from "@/components/online-reporting/report-view/utils";
import { createSurveyHandlers } from "@/components/online-reporting/survey-handlers";
import {
  createCondensedModel,
  ReportAttachment,
} from "@/components/online-reporting/utils";
import { AuthStateContext } from "@/contexts/AuthContext";
import { useDevEnvironment } from "@/contexts/DevContext";
import { APP_OR_SERVER_URL } from "@/environment";
import { reportColourTypes, statusTypes } from "@/types/typesReportV2";

interface RegistryEntryProps {
  mode?: "view" | "edit" | "new";
}

const RegistryEntry: React.FC<RegistryEntryProps> = ({ mode = "view" }) => {
  const { orgId, registryName, entryId } = useParams();

  // State for submission and survey data
  const [entryMetadata, setEntryMetadata] =
    useState<GetSubmissionResponsePortal>();
  const [surveyResult, setSurveyResult] = useState<GetSurveyResponse>();
  const [surveyModel, setSurveyModel] = useState<Model>();
  const [versionHistory, setVersionHistory] = useState<SubmissionVersionTime[]>(
    [],
  );
  const [selectedVersion, setSelectedVersion] = useState<string>();
  const [reportAttachments, setReportAttachments] = useState<
    Array<ReportAttachment>
  >([]);
  const [attachmentIDs, setAttachmentIDs] = useState<{ [key: string]: string }>(
    {},
  );

  // Add inline editing state
  const [isSaving, setIsSaving] = useState(false);
  const [isInlineEditMode, setIsInlineEditMode] = useState(false);
  const [originalEntryData, setOriginalEntryData] = useState<{
    [key: string]: any;
  }>({});

  const [showTranslated, setShowTranslated] = useState(true);

  const navigate = useNavigate();
  const { isDevEnv } = useDevEnvironment();

  // Add state for modal
  const [makePublicModalOpen, setMakePublicModalOpen] = useState(false);

  // Add new state for notes
  const [notesModified, setNotesModified] = useState(false);
  const [saveNotesLoading, setSaveNotesLoading] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);

  const incidentNumberRef = useRef<HTMLInputElement>();
  const notesRef = useRef<HTMLInputElement>();
  const synopsisRef = useRef<HTMLInputElement>();
  // Add inside the RegistryEntry component, near other state declarations
  const [serviceConfig, setServiceConfig] = useState<OrgConfigModel | null>(
    null,
  );

  /* Agency-defined shape for manually-entered RMS incident numbers. Null when
     the agency hasn't configured one, in which case any value is accepted. */
  const incidentNumberFormat = getIncidentNumberFormat(serviceConfig?.metadata);
  const [incidentNumberError, setIncidentNumberError] = useState<string | null>(
    null,
  );

  const incidentNumberHelperText = () => {
    if (incidentNumberError) return incidentNumberError;

    const autoGenerated =
      serviceConfig?.metadata.automated_incident_number_generation;

    const example = incidentNumberFormat
      ? describeIncidentNumberFormat(incidentNumberFormat)
      : null;

    if (example) {
      const format = `Format: ${example}`;
      return autoGenerated
        ? `${format} — auto-generated if left blank`
        : format;
    }

    return autoGenerated
      ? "Auto-generated if left blank; otherwise, provide a number to override"
      : "";
  };

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState("");
  const [emailTemplates, setEmailTemplates] = useState<Template[]>([]);

  const [isGeneratingSynopsis, setIsGeneratingSynopsis] = useState(false);

  const [numVersions, setNumVersions] = useState(0);
  const isClosed = isReportClosed(
    entryMetadata ?? ({} as GetSubmissionResponsePortal),
  );

  // Add context usage for assignment functionality
  const { currentUser } = useContext(AuthStateContext);
  const { rv2Users, fetchRv2Users } = useContext(OnlineReportingContext);

  // Add survey handlers for inline editing
  const organizationConfig = getServiceConfig(
    orgId ?? currentUser?.rubi_org_id ?? "NA",
    isDevEnv,
  );

  const handlers = createSurveyHandlers({
    id: entryId ?? "",
    appName: window.location.pathname.split("/")[1],
    attachmentIDs,
    setAttachmentIDs,
    organizationConfig,
    navigate,
  });

  const showExportCheckbox =
    !isClosed &&
    (!!serviceConfig?.export_configuration?.rms_export_configuration ||
      !!serviceConfig?.export_configuration?.dems_export_configuration);

  const filePreviewConfig = useFilePreviewConfig(
    serviceConfig,
    showExportCheckbox,
  );

  // Add inline editing functions
  const handleSave = async () => {
    if (!surveyModel) return;

    const hasErrors = surveyModel.hasErrors(true, false);
    if (hasErrors) {
      toast.error("Please resolve all errors before saving.");
      return;
    }

    // Switch back to display mode
    surveyModel.mode = "display";
    setIsInlineEditMode(false);

    setIsSaving(true);
    try {
      await handlers.submitForm(surveyModel, {
        showSaveInProgress: () => {
          console.log("Saving changes...");
        },
        showSaveSuccess: () => {
          console.log("Changes saved successfully.");
          toast.success("Registry entry updated successfully");
        },
        showSaveError: (error: any) => {
          console.error("Error saving changes:", error);
          toast.error("Failed to save changes");
        },
        currentStatus: entryMetadata?.status ?? "submitted",
      });

      window.location.reload();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save changes");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (surveyModel) {
      surveyModel.data = originalEntryData;
      surveyModel.mode = "display";
      setIsInlineEditMode(false);
      toast.info("Changes discarded");
    }
  };

  const EditButton = () => {
    const isEditMode = isInlineEditMode;

    const handleEditClick = async () => {
      if (surveyModel) {
        let editModel = surveyModel;
        if (showTranslated && entryMetadata?.translation_en) {
          const { newSurvey } = await buildViewModel(entryMetadata, false);
          editModel = newSurvey;
          setSurveyModel(newSurvey);
          setShowTranslated(false);
        }
        editModel.mode = "edit";

        FunctionFactory.Instance.register(
          "generateInternalId",
          (params: number[]) => {
            const existingValue = params[0];
            if (existingValue) {
              return existingValue;
            }
            return Math.trunc(Math.random() * 99999999).toString();
          },
        );

        setIsInlineEditMode(true);
      }
    };

    return (
      <Tooltip title={isEditMode ? "Save changes" : "Edit this registry entry"}>
        <span>
          <LoadingButton
            variant="contained"
            color={isEditMode ? "success" : "info"}
            onClick={isEditMode ? handleSave : handleEditClick}
            endIcon={isEditMode ? null : <Edit />}
            loading={isSaving}
            disabled={isClosed}
          >
            {isEditMode ? "Save" : "Edit"}
          </LoadingButton>
        </span>
      </Tooltip>
    );
  };

  const generateSynopsis = async () => {
    setIsGeneratingSynopsis(true);
    const response = (
      await getSubmissionSynopsisPortal({
        path: {
          submission_id: entryId ?? "",
        },
      })
    ).data!;
    setEntryMetadata((prev) => ({
      ...prev!,
      notes: notesRef?.current?.value ?? "",
      synopsis: response.synopsis,
      incident_number: incidentNumberRef?.current?.value ?? "",
    }));
    setNotesModified(true);
    setIsGeneratingSynopsis(false);
  };

  // Add function to update assignee
  const updateAssignee = async (userId: string | null) => {
    if (!entryId) return;

    try {
      const response = await patchSubmissionPortal({
        path: { submission_id: entryId },
        body: {
          assigned_to: userId
            ? { value: userId, action: "set" }
            : { action: "unset" },
        },
      });

      if (response.data?.assigned_to) {
        setEntryMetadata((prev) => ({
          ...prev!,
          notes: notesRef?.current?.value ?? "",
          synopsis: synopsisRef?.current?.value ?? "",
          incident_number: incidentNumberRef?.current?.value ?? "",
          assigned_to: response.data.assigned_to,
        }));
        toast.success("Assignee updated successfully");
      } else {
        setEntryMetadata((prev) => ({
          ...prev!,
          notes: notesRef?.current?.value ?? "",
          synopsis: synopsisRef?.current?.value ?? "",
          incident_number: incidentNumberRef?.current?.value ?? "",
          assigned_to: null,
        }));
        toast.success("Assignee removed successfully");
      }
    } catch (error) {
      console.error("Failed to update assignee:", error);
      toast.error("Failed to update assignee");
    }
  };

  const appName = window.location.pathname.split("/")[1];

  const buildViewModel = (
    submission: GetSubmissionResponsePortal,
    translated: boolean,
    pdfConfiguration = surveyResult?.survey.pdf_configuration || {},
  ) =>
    createCondensedModel(
      pdfConfiguration,
      (translated &&
        applyTranslation(submission.submission, submission.translation_en)) ||
        submission.submission,
      submission.attachments,
      true,
      false,
      false,
      submission.submission_id,
    );

  const handleTranslationToggle = async (translated: boolean) => {
    if (!entryMetadata || translated === showTranslated) return;
    const { newSurvey } = await buildViewModel(entryMetadata, translated);
    setSurveyModel(newSurvey);
    setShowTranslated(translated);
  };

  const loadEntry = async () => {
    if (!entryId) return;

    try {
      // Get submission data
      const submission = (
        await getSubmissionPortal({
          path: { submission_id: entryId },
          query: { version: "latest" },
        })
      ).data!;
      setEntryMetadata(submission);
      setVersionHistory(submission.version_times || []);
      setSelectedVersion(submission.latest_version?.toString());

      setAttachmentIDs(
        buildAttachmentIdMap(
          submission.submission,
          submission.attachments ?? [],
        ),
      );

      setNumVersions(
        submission.version_times?.filter(
          (version) => version.version <= (submission.latest_version || 0),
        ).length ?? 0,
      );

      // Get survey structure
      const survey = (
        await getSurvey({ path: { survey_id: submission.survey_id } })
      ).data!;
      setSurveyResult(survey);

      if (mode === "view") {
        const { newSurvey, reportAttachments } = await buildViewModel(
          submission,
          true,
          survey.survey.pdf_configuration,
        );
        setShowTranslated(true);
        setSurveyModel(newSurvey);
        setReportAttachments(reportAttachments);
      } else {
        const { newSurvey, reportAttachments } = await createCondensedModel(
          survey.survey,
          submission.submission,
          submission.attachments,
          true,
          false,
          false,
          entryId,
        );
        const newModel = new Model(survey.survey.configuration);
        newModel.data = submission.submission;
        setSurveyModel(newModel);
        setReportAttachments(reportAttachments);
      }

      setOriginalEntryData(submission.submission || {});
    } catch (error) {
      console.error("Error loading entry data:", error);
    }
  };

  useEffect(() => {
    registerAutocomplete();
    registerCameraRegistryMap();
    registerCurrencyInput();
    registerDriversLicenseScanner();
    registerEnhancedFileUpload();
    registerGasQuantityInput();
    registerNarrativeCanvas();
    loadEntry().then(() => fetchRv2Users(orgId ?? ""));
  }, [entryId]);

  useEffect(() => {
    if (!surveyModel) return;

    surveyModel.onComplete.clear();
    surveyModel.onComplete.add((sender, options) =>
      handlers.submitForm(sender, options),
    );
    surveyModel.onUploadFiles.clear();
    surveyModel.onUploadFiles.add((survey, options) =>
      handlers.uploadFiles(survey, options),
    );
    surveyModel.onClearFiles.clear();
    surveyModel.onClearFiles.add((_, options) => handlers.clearFiles(options));
  }, [surveyModel, handlers]);

  // Add after other useEffect hooks
  useEffect(() => {
    async function getConfig() {
      const response = (
        await getOrgConfigPortal({ path: { org_id: orgId ?? "" } })
      ).data!;
      setServiceConfig(response);
    }
    getConfig();
  }, [orgId]);

  // Add useEffect to load email templates
  useEffect(() => {
    async function getTemplates() {
      const response = (
        await getOrgTemplates({ path: { org_id: orgId ?? "" } })
      ).data!;
      setEmailTemplates(response.templates);
    }
    getTemplates();
  }, [orgId]);

  // Handle version changes
  const updateVersion = async (version: string) => {
    if (!entryId) return;

    try {
      const submission = (
        await getSubmissionPortal({
          path: { submission_id: entryId },
          query: { version },
        })
      ).data!;
      setEntryMetadata(submission);
      setSelectedVersion(version);

      const { newSurvey, reportAttachments } = await buildViewModel(
        submission,
        showTranslated,
      );
      setSurveyModel(newSurvey);
      setReportAttachments(reportAttachments);
    } catch (error) {
      console.error("Error loading version:", error);
    }
  };

  // Add modal details
  const modalDetails = {
    approved: {
      title: "Approve Entry",
      description: "Close this entry and mark it as approved.",
      detailsRequired: false,
      detailsText: "Optional notes for approval",
    },
    modification_requested: {
      title: "Follow-up",
      description: (
        <>
          <b>Keep the entry open</b> and ask for follow-up information from the
          reporting party. Mark as "Follow-up" and provide a link for the
          reporting party to submit their changes. Once they respond, it will be
          marked as "Pending."
        </>
      ),
      detailsRequired: true,
      detailsText: "Details to provide to the individual.",
    },
    pending_deletion: {
      title: "Remove Registration",
      description: "Mark this registration for removal.",
      detailsRequired: false,
      detailsText: "Optional notes for removal",
    },
    send_update: {
      title: "Send Update",
      description: "Send an update to the reporting party.",
      detailsRequired: true,
      detailsText: "Message",
    },
  };

  const sendUpdate = async (message: string) => {
    if (!entryId) return;

    try {
      await postSubmissionUpdateNotificationPortal({
        path: { submission_id: entryId },
        body: {
          message: message,
        },
      });
    } catch (error) {
      console.error("Error sending update:", error);
    }
    setModalOpen(false);
    toast.success("Update sent successfully");
  };

  // Add status update function
  const updateStatus = async (status: string, reason?: string) => {
    let newMetadata = entryMetadata ?? {};

    if (!isClosed && notesModified) {
      // Don't advance the status if the pending edits were rejected.
      if (!(await updateNotes())) return;
      newMetadata = {
        ...newMetadata,
        notes: notesRef?.current?.value ?? "",
        synopsis: synopsisRef?.current?.value ?? "",
        incident_number: (incidentNumberRef?.current?.value ?? "").trim(),
      };
    }

    if (
      serviceConfig?.metadata.automated_incident_number_generation &&
      serviceConfig?.metadata.incident_generation_outcomes?.includes(status)
    ) {
      try {
        const incidentNumberResponse = (
          await postSubmissionGenerateIncidentPortal({
            path: {
              submission_id: entryId ?? "",
            },
          })
        ).data!;

        newMetadata = {
          ...newMetadata,
          incident_number: incidentNumberResponse.incident_number,
        };
      } catch (error) {
        console.error("Incident number generation failed: ", error);
      }
    }
    try {
      if (reason) {
        if (status === "approved") {
          await patchSubmissionPortal({
            path: { submission_id: entryId ?? "" },
            body: { approve_note: reason },
          });
        } else if (status === "rejected") {
          await patchSubmissionPortal({
            path: { submission_id: entryId ?? "" },
            body: { reject_note: reason },
          });
        } else if (status === "modification_requested") {
          await patchSubmissionPortal({
            path: { submission_id: entryId ?? "" },
            body: { modification_request_note: reason },
          });
        }
      }

      await postSubmissionStatusPortal({
        path: { submission_id: entryId ?? "" },
        body: {
          status: status,
          notify_reporting_party: null,
        },
      });

      setEntryMetadata((prev) => ({
        ...prev!,
        ...newMetadata,
        status: status,
      }));

      toast.success("Entry status updated successfully");
      setModalOpen(false);
    } catch (error) {
      toast.error("Failed to update status");
    }
    // Shift to backend once background job system is completed
    const demsExportOutcomes = serviceConfig?.metadata
      ?.dems_export_outcomes ?? ["approved", "escalated"];
    if (
      demsExportOutcomes.includes(status) &&
      serviceConfig?.export_configuration?.dems_export_configuration
    ) {
      if (
        serviceConfig?.export_configuration?.dems_export_configuration
          .provider === "evidence.com"
      ) {
        postSubmissionExportEvidencePortal({
          path: { submission_id: entryId ?? "" },
        });
      }
    }
  };

  const restoreRegistration = async () => {
    if (!entryId || entryMetadata?.status !== "pending_deletion") return;

    try {
      await postSubmissionStatusPortal({
        path: { submission_id: entryId },
        body: {
          status: "approved",
        },
      });

      setEntryMetadata((prev) => ({
        ...prev!,
        status: "approved",
      }));

      toast.success("Registration restored successfully");
    } catch (error) {
      toast.error("Failed to restore registration");
    }
  };

  // Remove the ActionBar component and add getButtonGroups function
  const getButtonGroups = (): JSX.Element[][] => {
    const leftButtons: JSX.Element[] = [];
    const rightButtons: JSX.Element[] = [];

    // Add Edit button to left side if not rejected
    if (entryMetadata?.status !== "rejected") {
      leftButtons.push(<EditButton />);
    }

    if (isInlineEditMode) {
      leftButtons.push(
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancelEdit}
          disabled={isSaving}
        >
          Cancel
        </Button>,
      );
    }

    leftButtons.push(
      <Button
        variant="contained"
        color="info"
        onClick={() => {
          setSelectedAction("send_update");
          setModalOpen(true);
        }}
        startIcon={<Send />}
      >
        Send Update
      </Button>,
    );

    // Add Make Public button for private registrations
    if (entryMetadata?.private_registration) {
      leftButtons.push(
        <Button
          variant="contained"
          color="warning"
          onClick={() => setMakePublicModalOpen(true)}
        >
          Make Registration Public
        </Button>,
      );
    }

    if (
      entryMetadata?.status !== "modification_requested" &&
      !entryMetadata?.private_registration
    ) {
      rightButtons.push(
        <Button
          variant="contained"
          color="warning"
          onClick={() => {
            setSelectedAction("modification_requested");
            setModalOpen(true);
          }}
          startIcon={<Summarize />}
        >
          Follow-up
        </Button>,
      );
    }

    // Add action buttons based on status
    if (
      entryMetadata?.status === "submitted" ||
      entryMetadata?.status === "resubmitted"
    ) {
      rightButtons.push(
        <Button
          variant="contained"
          color="success"
          onClick={() => {
            setSelectedAction("approved");
            setModalOpen(true);
          }}
        >
          Approve RMS Export
        </Button>,
      );
    }
    if (entryMetadata?.status !== "pending_deletion") {
      rightButtons.push(
        <Button
          variant="contained"
          color="error"
          onClick={() => {
            setSelectedAction("pending_deletion");
            setModalOpen(true);
          }}
        >
          Remove
        </Button>,
      );
    } else {
      rightButtons.push(
        <Button variant="contained" color="info" onClick={restoreRegistration}>
          Restore Registration
        </Button>,
      );
    }

    return [leftButtons, rightButtons];
  };

  // Add notes update function
  /** Resolves to false when the save was rejected, so callers can abort. */
  const updateNotes = async () => {
    if (!entryId) return false;

    /* Single write chokepoint — covers both the Save button and a status
       change flushing pending edits. */
    const incidentNumber = (incidentNumberRef?.current?.value ?? "").trim();
    const formatError = validateIncidentNumber(
      incidentNumber,
      incidentNumberFormat,
    );
    if (formatError) {
      setIncidentNumberError(formatError);
      toast.error(formatError);
      return false;
    }

    setSaveNotesLoading(true);
    try {
      await patchSubmissionPortal({
        path: { submission_id: entryId },
        body: {
          notes: notesRef?.current?.value ?? "",
          synopsis: synopsisRef?.current?.value ?? "",
          incident_number: incidentNumber,
        },
      });

      setEntryMetadata((prev) => ({
        ...prev!,
        notes: notesRef?.current?.value ?? "",
        synopsis: synopsisRef?.current?.value ?? "",
        incident_number: incidentNumber,
      }));

      setNotesModified(false);
      toast.success("Notes updated successfully");
    } catch (error) {
      toast.error("Failed to update notes, please try again later");
    }
    setSaveNotesLoading(false);
    return true;
  };

  if (!entryMetadata || !surveyModel) {
    return (
      <div style={{ margin: "60px auto", textAlign: "center" }}>
        <CircularProgress />
      </div>
    );
  }

  // Add modal component before the final return statement
  const makePublicModal = (
    <Dialog
      open={makePublicModalOpen}
      onClose={() => setMakePublicModalOpen(false)}
    >
      <DialogTitle>Make Registration Public?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Making the registration public will notify the contacts of the
          registration that they now have access. Public users will now be able
          to submit updates.
          <br />
          <br />
          Do you want to proceed?
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setMakePublicModalOpen(false)}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            try {
              await patchSubmissionPortal({
                path: { submission_id: entryId ?? "" },
                body: {
                  private_registration: false,
                },
              });
              setEntryMetadata({
                ...entryMetadata,
                private_registration: false,
              });
              toast.success("Registration made public successfully");
              setMakePublicModalOpen(false);
            } catch (error) {
              console.error("Error making registration public:", error);
              toast.error("Failed to make registration public");
            }
          }}
          color="primary"
          variant="contained"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );

  const downloadPDF = async () => {
    let backend = APP_OR_SERVER_URL;
    if (organizationConfig?.apiRegion === "us") {
      backend = backend.replace("prod.api", "us.api");
    }
    window.open(
      `${backend}/report/portal/submission/${entryId}/pdf?combined=true`,
      "_self",
    );
  };

  const downloadPDFButton = () => {
    return (
      <Tooltip title="Download Registration" arrow>
        <Fab
          onClick={downloadPDF}
          color="secondary"
          size="small"
          aria-label="Download Registration"
        >
          <CloudDownload />
        </Fab>
      </Tooltip>
    );
  };

  const shareRegistryButton = () => {
    return (
      <Tooltip title="Share Registration" arrow>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setIsShareDialogOpen(true)}
          endIcon={<Share />}
        >
          Share Registration
        </Button>
      </Tooltip>
    );
  };

  const content = (
    <>
      <Button
        variant="outlined"
        color="primary"
        size="medium"
        startIcon={<ArrowBack />}
        onClick={() => navigate(-1)}
        style={{
          marginTop: "80px",
          height: "40px",
          position: "fixed",
          zIndex: 15,
          borderRadius: "0 10px 10px 0",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.30)",
          backgroundColor: "var(--background-paper)",
        }}
      >
        Back
      </Button>

      {/* Top Fixed Action Container with Assignment UI */}
      <PageHeader
        measureHeight
        left={
          <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
            <Chip
              label={
                statusTypes[entryMetadata?.status]
                  ? statusTypes[entryMetadata?.status]
                  : entryMetadata?.status
              }
              color={
                reportColourTypes[entryMetadata?.status]
                  ? reportColourTypes[entryMetadata?.status]
                  : "default"
              }
              size="medium"
            />
            <AssignedToSelector
              currentAssignee={entryMetadata?.assigned_to ?? null}
              currentUser={currentUser}
              onAssign={(user_id: string | null) => {
                updateAssignee(user_id);
              }}
              userOptions={rv2Users?.users ?? []}
              disableEdit={isClosed}
            />
            <div style={{ flex: 1 }}>
              <Typography
                variant="inherit"
                sx={{
                  textOverflow: "ellipsis",
                }}
              >
                Registry Entry - {registryName} - ({entryMetadata?.first_name}{" "}
                {entryMetadata?.last_name})
                {entryMetadata?.time_created && (
                  <Typography variant="body2" style={{ fontWeight: "bold" }}>
                    {new Date(entryMetadata.time_created).toLocaleString()}
                  </Typography>
                )}
              </Typography>
            </div>
          </div>
        }
        right={
          <div
            style={{
              display: "flex",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <Chip
              label={
                entryMetadata?.private_registration
                  ? "Police Controlled"
                  : "Public Controlled"
              }
              color={
                entryMetadata?.private_registration ? "warning" : "success"
              }
              size="medium"
              variant="outlined"
            />
            {shareRegistryButton()}
            {downloadPDFButton()}
          </div>
        }
      />

      <div
        style={{
          maxWidth: "960px",
          width: "100%",
          margin: "0 auto",
        }}
      >
        {/* Internal Notes Section */}
        <Paper elevation={3} style={{ padding: "20px", marginBottom: "20px" }}>
          {/* Add Incident Number Field */}
          <FormControl fullWidth style={{ marginBottom: "16px" }}>
            <TextField
              id="incidentNumber"
              key={entryMetadata?.incident_number}
              inputRef={incidentNumberRef}
              variant="outlined"
              label="RMS Incident / Occurrence Number"
              defaultValue={entryMetadata?.incident_number ?? ""}
              error={Boolean(incidentNumberError)}
              helperText={incidentNumberHelperText()}
              placeholder={
                serviceConfig?.metadata.automated_incident_number_generation
                  ? `Auto-generated when the record is ${serviceConfig.metadata.incident_generation_outcomes?.join(", ")}`
                  : "Enter Incident Number"
              }
              onChange={(e) => {
                setIncidentNumberError(
                  validateIncidentNumber(e.target.value, incidentNumberFormat),
                );
                if (!notesModified) {
                  setNotesModified(true);
                }
              }}
            />
          </FormControl>

          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Internal Private Notes
          </Typography>

          {/* Existing Notes TextField */}
          <TextField
            fullWidth
            multiline
            minRows={4}
            inputRef={notesRef}
            defaultValue={entryMetadata?.notes ?? ""}
            onChange={() => {
              if (!notesModified) {
                setNotesModified(true);
              }
            }}
            variant="outlined"
          />
          <div>
            <Typography variant="h6">
              Synopsis
              <LoadingButton
                variant="contained"
                color="primary"
                size="medium"
                sx={{ margin: 2 }}
                onClick={() => generateSynopsis()}
                loading={isGeneratingSynopsis}
              >
                {synopsisRef.current?.value ? (
                  <>Regenerate</>
                ) : (
                  <>
                    Generate <AutoFixHighRounded />
                  </>
                )}
              </LoadingButton>
            </Typography>
            {isGeneratingSynopsis ? (
              <div
                style={{
                  border: "1px solid lightgrey",
                  borderRadius: "4px",
                  padding: "8px",
                  marginTop: "16px",
                }}
              >
                <Skeleton variant="text" />
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </div>
            ) : (
              <FormControl fullWidth>
                <TextField
                  id="synopsis"
                  variant="outlined"
                  inputRef={synopsisRef}
                  multiline
                  placeholder="No synopsis generated yet."
                  helperText={
                    synopsisRef.current?.value
                      ? "Generated synopsis may contain errors. Please review and update important information."
                      : ""
                  }
                  defaultValue={entryMetadata?.synopsis ?? ""}
                  minRows={4}
                  onChange={() => {
                    if (!notesModified) {
                      setNotesModified(true);
                    }
                  }}
                />
              </FormControl>
            )}
          </div>
          <LoadingButton
            variant="contained"
            color="info"
            size="medium"
            fullWidth
            sx={{ mt: 2 }}
            onClick={updateNotes}
            loading={saveNotesLoading}
            disabled={!notesModified || Boolean(incidentNumberError)}
          >
            Save Changes
          </LoadingButton>
        </Paper>

        {/* Survey display with version selector */}
        <div
          className={mode === "view" ? "condensed-report-wrapper" : ""}
          style={{ marginBottom: "20px" }}
        >
          <Paper elevation={3} style={{ padding: "20px" }}>
            {/* Version selector moved here */}
            {versionHistory.length > 1 && numVersions > 1 && (
              <FormControl fullWidth style={{ marginBottom: "20px" }}>
                <Typography variant="subtitle1">Version</Typography>
                <Select
                  value={selectedVersion}
                  onChange={(e) => updateVersion(e.target.value)}
                  variant="outlined"
                >
                  {versionHistory
                    .filter(
                      (version) =>
                        version.version <= (entryMetadata.latest_version || 0),
                    )
                    .sort((a, b) => b.version - a.version)
                    .map((version) => (
                      <MenuItem key={version.version} value={version.version}>
                        {version.version === entryMetadata.latest_version ? (
                          <span>
                            <strong>Latest Version</strong> -{" "}
                            {formatDateShort(version.time_created)}
                          </span>
                        ) : (
                          <span>
                            Version {version.version} -{" "}
                            {formatDateShort(version.time_created)}
                          </span>
                        )}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            )}
            {mode === "view" && hasForeignLocale(entryMetadata.locale) && (
              <TranslationToggle
                locale={entryMetadata.locale!}
                showTranslated={showTranslated}
                onChange={handleTranslationToggle}
                disabled={isInlineEditMode}
              />
            )}
            <Survey
              key={`survey-${isInlineEditMode ? "edit" : "view"}`}
              model={surveyModel}
            />
          </Paper>
        </div>
      </div>

      <BottomFixedActionContainer buttonGroups={getButtonGroups()} />
      {makePublicModal}
      <StatusActionModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        selectedAction={selectedAction}
        onSubmit={(reason) => {
          if (selectedAction === "send_update") {
            sendUpdate(reason);
          } else {
            updateStatus(selectedAction, reason);
          }
        }}
        modalDetails={modalDetails}
        emailTemplates={emailTemplates}
      />
      <ShareReportDialog
        isShareDialogOpen={isShareDialogOpen}
        setIsShareDialogOpen={setIsShareDialogOpen}
        appName={appName}
        orgId={orgId ?? ""}
        id={entryId ?? ""}
      />
    </>
  );

  return (
    <FilePreviewConfigContext.Provider value={filePreviewConfig}>
      {content}
    </FilePreviewConfigContext.Provider>
  );
};

export default RegistryEntry;
